/**
 * Client-only wishlist (decision: kept in localStorage for this
 * version, no backend, no customer account requirement). Keyed on
 * real Shopify product/variant IDs instead of the old demo-catalogue
 * `id` field, so it's ready for the real catalogue once it exists.
 *
 * Responsibilities:
 *  - Add / remove / toggle a product, keep the count in sync.
 *  - Wishlist drawer (snippets/wishlist-drawer.liquid): open/close,
 *    render from localStorage, remove item, "move to bag" (adds the
 *    saved variant to the real Shopify cart via /cart/add.js, then
 *    hands off to assets/cart.js to refresh/open the cart drawer).
 *  - Sync any [data-wishlist-toggle] button's active state/label
 *    (used by the PDP wishlist button today; safe to reuse on future
 *    product-card hearts without changes).
 *  - Persists across page refresh — it's just localStorage.
 */
(function () {
  'use strict';

  var STORAGE_KEY = 'anmol_wishlist';

  function qs(sel, ctx) {
    return (ctx || document).querySelector(sel);
  }
  function qsa(sel, ctx) {
    return Array.prototype.slice.call((ctx || document).querySelectorAll(sel));
  }

  function read() {
    try {
      return JSON.parse(localStorage.getItem(STORAGE_KEY)) || [];
    } catch (e) {
      return [];
    }
  }
  function write(list) {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(list));
    updateBadges();
  }

  function isWishlisted(productId) {
    return read().some(function (i) {
      return i.id === productId;
    });
  }
  function addToWishlist(item) {
    var list = read();
    if (!list.some(function (i) { return i.id === item.id; })) {
      list.push(item);
      write(list);
    }
  }
  function removeFromWishlist(productId) {
    write(
      read().filter(function (i) {
        return i.id !== productId;
      })
    );
  }
  function toggleWishlist(item) {
    if (isWishlisted(item.id)) {
      removeFromWishlist(item.id);
      return false;
    }
    addToWishlist(item);
    return true;
  }
  function wishlistCount() {
    return read().length;
  }

  function updateBadges() {
    var count = wishlistCount();
    qsa('.wishlist-badge').forEach(function (badge) {
      badge.textContent = count;
      badge.style.display = count > 0 ? 'flex' : 'none';
    });
  }

  function formatMoney(cents) {
    var amount = (Number(cents) || 0) / 100;
    try {
      return new Intl.NumberFormat(document.documentElement.lang || 'en-IN', {
        style: 'currency',
        currency: 'INR',
        maximumFractionDigits: 0
      }).format(amount);
    } catch (e) {
      return '₹' + amount.toLocaleString('en-IN');
    }
  }

  function syncToggleButtons(productId) {
    var active = isWishlisted(productId);
    qsa('[data-wishlist-toggle][data-product-id="' + productId + '"]').forEach(function (btn) {
      btn.classList.toggle('active', active);
      var label = btn.querySelector('[data-wishlist-toggle-label]');
      if (label) label.textContent = active ? btn.dataset.savedLabel || 'Saved to Wishlist' : btn.dataset.addLabel || 'Add to Wishlist';
    });
  }

  // ── Drawer open / close ──
  function drawerEls() {
    return { overlay: qs('[data-wishlist-overlay]'), drawer: qs('[data-wishlist-drawer]') };
  }
  function openWishlistDrawer() {
    var els = drawerEls();
    if (!els.drawer) return;
    els.overlay.classList.add('open');
    els.drawer.classList.add('open');
    document.body.style.overflow = 'hidden';
    renderWishlistDrawer();
  }
  function closeWishlistDrawer() {
    var els = drawerEls();
    if (els.overlay) els.overlay.classList.remove('open');
    if (els.drawer) els.drawer.classList.remove('open');
    document.body.style.overflow = '';
  }

  function buildWhatsAppEnquiryUrl(drawer, item) {
    var number = drawer.dataset.whatsappNumber || '';
    var prefix = drawer.dataset.whatsappPrefix || '';
    var suffix = drawer.dataset.whatsappSuffix || '';
    var message = prefix + ' ' + (item.title || '');
    if (item.price) message += ' (Price: ' + formatMoney(item.price) + ')';
    message += '. ' + suffix + ' ' + (item.url || '');
    return 'https://wa.me/' + number + '?text=' + encodeURIComponent(message);
  }

  function renderWishlistDrawer() {
    var drawer = qs('[data-wishlist-drawer]');
    var body = qs('[data-wishlist-body]');
    if (!drawer || !body) return;
    var items = read();

    if (!items.length) {
      body.innerHTML = '<div class="commerce-empty">' + (drawer.dataset.emptyLabel || 'Nothing saved yet.') + '</div>';
      return;
    }

    var template = qs('[data-wishlist-item-template]');
    body.innerHTML = '';
    items.forEach(function (item) {
      var node = template.content.cloneNode(true);
      var root = node.querySelector('[data-wishlist-item]');
      root.setAttribute('data-product-id', item.id);
      var isEnquiryOnly = item.enquiryOnly === true;
      root.setAttribute('data-enquiry-only', isEnquiryOnly);

      var img = node.querySelector('[data-wishlist-item-image]');
      if (img) {
        img.src = item.image || '';
        img.alt = item.title || '';
      }
      var titleEl = node.querySelector('[data-wishlist-item-title]');
      if (titleEl) titleEl.textContent = item.title || '';
      var priceEl = node.querySelector('[data-wishlist-item-price]');
      if (priceEl) priceEl.textContent = item.price ? formatMoney(item.price) : '';

      var moveBtn = node.querySelector('[data-wishlist-item-move]');
      var enquireLink = node.querySelector('[data-wishlist-item-enquire]');
      if (isEnquiryOnly) {
        if (moveBtn) moveBtn.remove();
        if (enquireLink) {
          enquireLink.href = buildWhatsAppEnquiryUrl(drawer, item);
          enquireLink.textContent = drawer.dataset.enquireLabel || 'Enquire on WhatsApp';
        }
      } else {
        if (enquireLink) enquireLink.remove();
      }

      body.appendChild(node);
    });
  }

  function moveToBag(productId) {
    var item = read().filter(function (i) {
      return i.id === productId;
    })[0];
    // Defensive: enquiry-only products must never reach /cart/add.js,
    // even from a stale wishlist entry or manual DOM manipulation —
    // the "Move to Bag" control is never rendered for them, but this
    // guard is the enforcement point regardless of how the call happens.
    if (item && item.enquiryOnly === true) return;

    removeFromWishlist(productId);
    renderWishlistDrawer();
    syncToggleButtons(productId);
    if (!item || !item.variantId) return;

    fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ id: item.variantId, quantity: 1 })
    }).then(function () {
      if (window.AnmolCart) {
        window.AnmolCart.refresh();
        window.AnmolCart.open();
      }
    });
  }

  document.addEventListener('click', function (e) {
    if (e.target.closest('[data-wishlist-close]') || e.target.closest('[data-wishlist-overlay]')) {
      closeWishlistDrawer();
      return;
    }

    var toggleBtn = e.target.closest('[data-wishlist-toggle]');
    if (toggleBtn) {
      e.preventDefault();
      var item = {
        id: toggleBtn.dataset.productId,
        variantId: toggleBtn.dataset.variantId || '',
        handle: toggleBtn.dataset.productHandle || '',
        title: toggleBtn.dataset.productTitle || '',
        price: toggleBtn.dataset.productPrice ? parseInt(toggleBtn.dataset.productPrice, 10) : 0,
        image: toggleBtn.dataset.productImage || '',
        url: toggleBtn.dataset.productUrl || '',
        enquiryOnly: toggleBtn.dataset.enquiryOnly === 'true'
      };
      var active = toggleWishlist(item);
      toggleBtn.classList.toggle('active', active);
      var label = toggleBtn.querySelector('[data-wishlist-toggle-label]');
      if (label) label.textContent = active ? toggleBtn.dataset.savedLabel || 'Saved to Wishlist' : toggleBtn.dataset.addLabel || 'Add to Wishlist';
      renderWishlistDrawer();
      return;
    }

    var itemRoot = e.target.closest('[data-wishlist-item]');
    if (!itemRoot) return;
    var productId = itemRoot.getAttribute('data-product-id');

    if (e.target.closest('[data-wishlist-item-remove]')) {
      removeFromWishlist(productId);
      renderWishlistDrawer();
      syncToggleButtons(productId);
    } else if (e.target.closest('[data-wishlist-item-move]')) {
      moveToBag(productId);
    }
  });

  document.addEventListener('keydown', function (e) {
    if (e.key === 'Escape') closeWishlistDrawer();
  });

  function wireWishlistTriggers() {
    qsa('[data-wishlist-toggle-drawer], .nav-icon-btn[aria-label="Wishlist"]').forEach(function (btn) {
      btn.addEventListener('click', function (e) {
        e.preventDefault();
        openWishlistDrawer();
      });
    });
  }

  function initToggleButtonStates() {
    qsa('[data-wishlist-toggle]').forEach(function (btn) {
      syncToggleButtons(btn.dataset.productId);
    });
  }

  window.AnmolWishlist = {
    open: openWishlistDrawer,
    close: closeWishlistDrawer,
    isWishlisted: isWishlisted,
    toggle: toggleWishlist,
    count: wishlistCount
  };

  document.addEventListener('DOMContentLoaded', function () {
    wireWishlistTriggers();
    initToggleButtonStates();
    updateBadges();
  });
})();
